// High-performance 2D canvas particle-network engine.
// Simulated nodes + distance-based connections + traveling packets,
// with mouse repulsion, scroll distortion and per-section "modes".
// Designed for ~60fps with 60–110 nodes. Reduced-motion friendly.

const MODES = {
  default: { count: 78, maxDist: 150, packetRate: 0.6, lineAlpha: 0.5, hue: 82 },
  dense: { count: 105, maxDist: 172, packetRate: 1.4, lineAlpha: 0.62, hue: 82 },
  packets: { count: 92, maxDist: 160, packetRate: 2.6, lineAlpha: 0.6, hue: 145 },
  embeddings: { count: 110, maxDist: 132, packetRate: 0.5, lineAlpha: 0.38, hue: 190 },
  clusters: { count: 96, maxDist: 150, packetRate: 1.1, lineAlpha: 0.55, hue: 82 },
};

function rand(a, b) {
  return a + Math.random() * (b - a);
}

export class NetworkEngine {
  constructor(canvas, opts = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d", { alpha: true });
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.opts = opts;
    this.mode = { ...MODES.default };
    this.targetMode = MODES.default;
    this.particles = [];
    this.packets = [];
    this.mouse = { x: -9999, y: -9999, active: false };
    this.scroll = { v: 0, y: 0 };
    this.running = false;
    this.spawnRatio = opts.spawnRatio != null ? opts.spawnRatio : 1; // 0..1 for intro growth
    this.last = 0;
    this.raf = null;
    this._onVis = () => (document.hidden ? this.stop() : this.start());
    this.resize();
  }

  resize() {
    const { canvas } = this;
    const w = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth;
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight;
    this.w = w;
    this.h = h;
    canvas.width = Math.floor(w * this.dpr);
    canvas.height = Math.floor(h * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this._ensureParticles();
  }

  _ensureParticles() {
    const want = Math.round(this.targetMode.count * (this.opts.densityScale || 1));
    while (this.particles.length < want) {
      const z = rand(0.35, 1);
      this.particles.push({
        x: rand(0, this.w),
        y: rand(0, this.h),
        vx: rand(-0.14, 0.14) * z,
        vy: rand(-0.14, 0.14) * z,
        z,
        r: rand(0.8, 2.4) * z,
        pulse: Math.random() * Math.PI * 2,
        hot: 0,
      });
    }
    if (this.particles.length > want) this.particles.length = want;
  }

  setMode(name) {
    this.targetMode = MODES[name] || MODES.default;
    this._ensureParticles();
  }
  setMouse(x, y, active) {
    this.mouse.x = x;
    this.mouse.y = y;
    this.mouse.active = active;
  }
  setScroll(v, y) {
    this.scroll.v = v;
    this.scroll.y = y;
  }
  setSpawnRatio(r) {
    this.spawnRatio = Math.max(0, Math.min(1, r));
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    document.addEventListener("visibilitychange", this._onVis);
    const loop = (t) => {
      if (!this.running) return;
      const dt = Math.min(40, t - this.last) / 16.67;
      this.last = t;
      this.step(dt);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    document.removeEventListener("visibilitychange", this._onVis);
  }

  // Ease current mode params toward target for smooth section transitions.
  _lerpMode(dt) {
    const m = this.mode;
    const t = this.targetMode;
    const k = 1 - Math.pow(0.9, dt);
    m.maxDist += (t.maxDist - m.maxDist) * k;
    m.packetRate += (t.packetRate - m.packetRate) * k;
    m.lineAlpha += (t.lineAlpha - m.lineAlpha) * k;
    m.hue += (t.hue - m.hue) * k;
  }

  step(dt) {
    const { ctx, w, h, mode, mouse } = this;
    this._lerpMode(dt);
    ctx.clearRect(0, 0, w, h);

    const visible = Math.round(this.particles.length * this.spawnRatio);
    const parts = this.particles;
    const scrollPush = this.scroll.v * 0.4;

    // Update particles
    for (let i = 0; i < visible; i++) {
      const p = parts[i];
      p.x += p.vx * dt;
      p.y += (p.vy + scrollPush * (p.z - 0.5) * 0.05) * dt;
      p.pulse += 0.02 * dt;
      p.hot *= 0.94;

      // Mouse repulsion + energy
      if (mouse.active) {
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const d2 = dx * dx + dy * dy;
        const R = 150;
        if (d2 < R * R) {
          const d = Math.sqrt(d2) || 1;
          const f = (1 - d / R) * 1.7;
          p.x += (dx / d) * f * dt;
          p.y += (dy / d) * f * dt;
          p.hot = Math.min(1, p.hot + 0.06);
        }
      }

      // Wrap around edges softly
      if (p.x < -20) p.x = w + 20;
      if (p.x > w + 20) p.x = -20;
      if (p.y < -20) p.y = h + 20;
      if (p.y > h + 20) p.y = -20;
    }

    // Connections
    const maxD = mode.maxDist;
    for (let i = 0; i < visible; i++) {
      const a = parts[i];
      for (let j = i + 1; j < visible; j++) {
        const b = parts[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > maxD * maxD) continue;
        const d = Math.sqrt(d2);
        const alpha = (1 - d / maxD) * mode.lineAlpha * Math.min(a.z, b.z);
        const boost = Math.max(a.hot, b.hot);
        ctx.strokeStyle = `hsla(${mode.hue}, ${70 + boost * 20}%, ${58 + boost * 12}%, ${alpha + boost * 0.3})`;
        ctx.lineWidth = 0.5 + Math.min(a.z, b.z) * 0.7 + boost * 0.8;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();

        // Occasionally emit a packet along a connection
        if (Math.random() < 0.00035 * mode.packetRate * dt && this.packets.length < 40) {
          this.packets.push({ ax: a.x, ay: a.y, bx: b.x, by: b.y, t: 0, sp: rand(0.02, 0.05) });
        }
      }
    }

    // Packets
    for (let i = this.packets.length - 1; i >= 0; i--) {
      const pk = this.packets[i];
      pk.t += pk.sp * dt;
      if (pk.t >= 1) {
        this.packets.splice(i, 1);
        continue;
      }
      const x = pk.ax + (pk.bx - pk.ax) * pk.t;
      const y = pk.ay + (pk.by - pk.ay) * pk.t;
      ctx.fillStyle = `hsla(${mode.hue}, 90%, 68%, ${1 - pk.t})`;
      ctx.beginPath();
      ctx.arc(x, y, 1.8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Nodes
    for (let i = 0; i < visible; i++) {
      const p = parts[i];
      const glow = 0.5 + Math.sin(p.pulse) * 0.25 + p.hot;
      ctx.beginPath();
      ctx.fillStyle = `hsla(${mode.hue}, 80%, ${62 + p.hot * 20}%, ${(0.45 + p.hot * 0.5) * p.z})`;
      ctx.arc(p.x, p.y, p.r + p.hot * 1.5, 0, Math.PI * 2);
      ctx.fill();
      if (p.hot > 0.15) {
        ctx.beginPath();
        ctx.fillStyle = `hsla(${mode.hue}, 90%, 70%, ${p.hot * 0.14})`;
        ctx.arc(p.x, p.y, (p.r + 6) * glow, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  destroy() {
    this.stop();
    this.particles = [];
    this.packets = [];
  }
}

export { MODES };
